const finitePosition=position=>position&&['x','y','z'].every(axis=>Number.isFinite(position[axis]));

/** The first valid viewer position is the user's neutral seated/standing pose.
 * Keep it through transient tracking/visibility loss, so reacquisition retains
 * physical movement rather than silently changing the user's height. A native
 * reference-space reset must call reset(); a session end must call clear().
 * A replacement session/reference space also invalidates the old calibration.
 * Orientation remains entirely tracked by WebXR, not part of calibration. */
export function createXRStandingCalibration({THREE:T}) {
  let neutral=null,viewerPosition=null,viewerQuaternion=null,tracked=false,currentSession=null,currentReferenceSpace=null;
  const reset=()=>{neutral=null;viewerPosition=null;viewerQuaternion=null;tracked=false;};
  return {
    get neutralPosition(){return neutral?.clone()??null;},
    get tracked(){return tracked;},
    get viewerPosition(){return viewerPosition?.clone()??null;},
    get viewerQuaternion(){return viewerQuaternion?.clone()??null;},
    reset,
    clear(){reset();currentSession=null;currentReferenceSpace=null;},
    sample(frame,session,referenceSpace){
      tracked=false;viewerPosition=null;viewerQuaternion=null;
      if(!session||!referenceSpace)return false;
      if(session!==currentSession||referenceSpace!==currentReferenceSpace){
        reset();currentSession=session;currentReferenceSpace=referenceSpace;
      }
      if(!frame||session.visibilityState!=='visible')return false;
      try {
        const transform=frame.getViewerPose(referenceSpace)?.transform,position=transform?.position;
        if(!finitePosition(position))return false;
        viewerPosition=new T.Vector3(position.x,position.y,position.z);
        const orientation=transform.orientation;
        if(orientation&&['x','y','z','w'].every(axis=>Number.isFinite(orientation[axis]))&&Math.hypot(orientation.x,orientation.y,orientation.z,orientation.w)>1e-8)
          viewerQuaternion=new T.Quaternion(orientation.x,orientation.y,orientation.z,orientation.w).normalize();
        if(!neutral)neutral=viewerPosition.clone();
        tracked=true;
      }catch{/* A missing pose or expired XRFrame never calibrates the rig. */}
      return tracked;
    },
  };
}

/** Compose the user's tracked pose onto the rendered rider chart. Both eyes
 * and grips inherit this same neutral-position translation. The full rider
 * quaternion is kept intact: upside-down riding is still first person.
 * The legacy default serves standalone callers only; immersive presentation
 * waits for calibration and always supplies the actual neutralPosition. */
export function setXRAnchor({THREE:T,rig,camera,unitsPerMeter=1,neutralPosition={x:0,y:1.65,z:0},fullPose=camera.userData.valoriaFullRiderPose===true}) {
  if(!Number.isFinite(unitsPerMeter)||unitsPerMeter<=0)throw TypeError('Positive XR render scale required');
  if(!finitePosition(neutralPosition))throw TypeError('Finite XR neutral position required');
  rig.scale.setScalar(unitsPerMeter);
  if(fullPose)rig.quaternion.copy(camera.quaternion).normalize();
  else {const angles=new T.Euler().setFromQuaternion(camera.quaternion,'YXZ');rig.rotation.set(0,angles.y,0);}
  const baseline=new T.Vector3(neutralPosition.x,neutralPosition.y,neutralPosition.z).multiplyScalar(unitsPerMeter).applyQuaternion(rig.quaternion);
  rig.position.copy(camera.position).sub(baseline);rig.updateMatrixWorld(true);
  return rig;
}
/** Grip poses, not pointer-ray poses, drive the hands. No stale tracking is
 * reused after a missing pose, controller loss, hidden session or session end. */
export function createXRGripTracking({THREE:T}) {
  let raw={};
  return {
    clear(){raw={};},
    sample(frame,session,referenceSpace){
      raw={};if(!frame||!session||session.visibilityState!=='visible'||!referenceSpace)return;
      for(const source of session.inputSources??[]){const hand=source.handedness;if(!['left','right'].includes(hand)||!source.gripSpace)continue;
        try{const pose=frame.getPose(source.gripSpace,referenceSpace),values=pose?.transform?.matrix;if(!values||values.length!==16||!Array.from(values).every(Number.isFinite))continue;
          const matrix=new T.Matrix4().fromArray(values);if(Math.abs(matrix.determinant())<1e-8)continue;raw[hand]=matrix;
        }catch{/* A disappeared grip or ended frame is an untracked hand. */}
      }
    },
    worldPoses(rig){const poses={};for(const [hand,local] of Object.entries(raw)){const matrix=new T.Matrix4().multiplyMatrices(rig.matrixWorld,local),position=new T.Vector3(),quaternion=new T.Quaternion(),scale=new T.Vector3();matrix.decompose(position,quaternion,scale);poses[hand]={position,quaternion:quaternion.normalize()};}return poses;},
  };
}
